import { QUEUE_NAMES, REPLAY_REFUSAL_MESSAGES, type DeadLetterJob, type QueueName } from '@rp/contracts'
import { Link } from '@tanstack/react-router'
import { failureText } from '../../api/copy'
import { Button, Chip, LedgerRow } from '../../design'
import { formatDateTime } from '../../lib/time'
import { OPERATIONS_COPY, QUEUE_LABELS, REPLAY_OUTCOME_COPY } from './copy'

export type DeadLetterListProps = {
  jobs: readonly DeadLetterJob[]
  total: number
  pendingId: string | null
  onReplay: (jobId: string) => void
}

type RowProps = { job: DeadLetterJob; pending: boolean; onReplay: (jobId: string) => void }

const isQueueName = (queue: string): queue is QueueName => (QUEUE_NAMES as readonly string[]).includes(queue)

const queueLabelOf = (queue: string): string => (isQueueName(queue) ? QUEUE_LABELS[queue] : queue)

const ReplayCell = ({ job, pending, onReplay }: RowProps) => {
  if (job.replayedAt !== null) {
    return (
      <span className="ops-row-note">
        {OPERATIONS_COPY.replayedNote(
          formatDateTime(new Date(job.replayedAt)),
          job.replayOutcome === null ? null : REPLAY_OUTCOME_COPY[job.replayOutcome],
        )}
      </span>
    )
  }
  if (job.refusalReason !== null) {
    return <span className="ops-row-note">{REPLAY_REFUSAL_MESSAGES[job.refusalReason]}</span>
  }
  return (
    <Button variant="primary" pending={pending} pendingLabel={OPERATIONS_COPY.replaying} onClick={() => onReplay(job.id)}>
      {OPERATIONS_COPY.replay}
    </Button>
  )
}

const DeadLetterRow = ({ job, pending, onReplay }: RowProps) => (
  <LedgerRow>
    <div className="ops-row">
      <span className="ops-row-code">{queueLabelOf(job.queue)}</span>
      <Chip tone={job.replayable ? 'warning' : 'neutral'}>
        {OPERATIONS_COPY.retryCount}：{job.retryCount}
      </Chip>
      {job.replayOf === null ? null : (
        <Chip tone="danger" variant="outline">
          {OPERATIONS_COPY.replayRepeat}
        </Chip>
      )}
      <span className="ops-row-time num">
        {OPERATIONS_COPY.failedAt}：{formatDateTime(new Date(job.failedAt))}
      </span>
      {job.failureCode === null ? null : <p className="ops-row-reason tone-danger">{failureText(job.failureCode)}</p>}
      <div className="ops-row-action">
        <ReplayCell job={job} pending={pending} onReplay={onReplay} />
      </div>
      {job.conversationId === null ? null : (
        <Link to="/items/$itemId" params={{ itemId: job.conversationId }} className="ops-row-link">
          {OPERATIONS_COPY.openItem}
        </Link>
      )}
    </div>
  </LedgerRow>
)

export const DeadLetterList = ({ jobs, total, pendingId, onReplay }: DeadLetterListProps) => (
  <section className="ops-section">
    <div className="ops-section-head">
      <h2>{OPERATIONS_COPY.deadLetterTitle}</h2>
      {jobs.length === 0 ? null : <p className="ops-section-summary">{OPERATIONS_COPY.deadLetterSummary(total)}</p>}
    </div>
    {jobs.length === 0 ? (
      <p className="ops-empty">{OPERATIONS_COPY.deadLetterEmpty}</p>
    ) : (
      <ul className="ledger-list ops-list" aria-label={OPERATIONS_COPY.deadLetterTitle}>
        {jobs.map((job) => (
          <DeadLetterRow key={job.id} job={job} pending={pendingId === job.id} onReplay={onReplay} />
        ))}
      </ul>
    )}
  </section>
)
